import { cn } from "@/utils";
import { FileRecordTypeIcon, IntegrationIcon } from "@cohesive/ui-components";
import { useDndContext } from "@dnd-kit/core";
import {
  Ban,
  Copy,
  Files,
  Folder,
  Frame,
  MessagesSquare,
  Move,
  Plus,
  Star,
} from "lucide-react";
import { buildWorkspaceMoves } from "./buildIntents";
import { classifyTransfer } from "./classifyTransfer";
import {
  classifyMixedWorkspaceDetailDrop,
  isMixedWorkspaceDetailSelection,
} from "./handlers/workspaceDetailDrop";
import { asDraggableData, asDroppableData } from "./ids";

export function TransferDragPill() {
  const { active, over } = useDndContext();
  if (!active) return null;

  const selectionSnapshot = active.data.current?.selectionSnapshot as
    | string[]
    | undefined;
  const draggable = asDraggableData(active.data.current);
  if (!draggable) {
    if (
      selectionSnapshot &&
      selectionSnapshot.length > 0 &&
      isMixedWorkspaceDetailSelection(selectionSnapshot)
    ) {
      const droppable = over ? asDroppableData(over.data.current) : null;
      const mixedClassification = droppable
        ? classifyMixedWorkspaceDetailDrop(droppable, selectionSnapshot)
        : null;
      const count = selectionSnapshot.length;
      const isInvalid = mixedClassification?.kind === "invalid";
      const label = isInvalid
        ? mixedClassification.reason
        : `Move ${count} items`;

      return (
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-3.5 py-2",
            "text-xs font-medium shadow-xl shadow-black/25 pointer-events-none",
            "backdrop-blur-sm transition-colors duration-100 max-w-[280px]",
            isInvalid
              ? "bg-foreground/45 text-background/70"
              : "bg-foreground/95 text-background",
          )}
        >
          {isInvalid ? (
            <Ban className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <Move className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate">{label}</span>
        </div>
      );
    }
    return null;
  }

  const droppable = over ? asDroppableData(over.data.current) : null;

  // No-op has no variant of its own: a redundant drop renders the neutral pill
  // (name, no verb) so the pill never vanishes mid-drag.
  type Variant = "copy" | "move" | "place" | "favorite" | "invalid" | "neutral";
  let variant: Variant = "neutral";

  let label: string;
  let leadVerb = "Copy";
  let isSection = false;
  let isFolderBundle = false;

  if (draggable.kind === "entity") {
    label =
      draggable.entities.length === 1
        ? draggable.primaryName
        : `${draggable.entities.length} items`;
    variant = "place";
    leadVerb = "Place";
    if (droppable?.surface === "canvas-section") {
      label = `${label} into section`;
    }
    const Icon = Plus;
    return (
      <div
        className={cn(
          "inline-flex items-center gap-2 rounded-full px-3.5 py-2",
          "text-xs font-medium shadow-xl shadow-black/25 pointer-events-none",
          "backdrop-blur-sm transition-colors duration-100 max-w-[280px]",
          "bg-emerald-600/95 text-white",
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{`${leadVerb} ${label}`}</span>
      </div>
    );
  }

  if (draggable.kind === "skill") {
    label = draggable.primaryName;
    if (droppable) {
      const classification = classifyTransfer(draggable, droppable.destination);
      if (classification.kind === "favorite") {
        return (
          <div
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3.5 py-2",
              "text-xs font-medium shadow-xl shadow-black/25 pointer-events-none",
              "backdrop-blur-sm transition-colors duration-100 max-w-[280px]",
              "bg-amber-500/95 text-white",
            )}
          >
            <Star className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{`Star ${label}`}</span>
          </div>
        );
      }
      if (
        droppable.surface === "canvas-board" ||
        droppable.surface === "canvas-section" ||
        droppable.surface === "canvas-scope" ||
        droppable.surface === "files-rail-canvas-scope"
      ) {
        if (droppable.surface === "canvas-section") {
          label = `${label} into section`;
        }
        return (
          <div
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3.5 py-2",
              "text-xs font-medium shadow-xl shadow-black/25 pointer-events-none",
              "backdrop-blur-sm transition-colors duration-100 max-w-[280px]",
              "bg-emerald-600/95 text-white",
            )}
          >
            <Plus className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{`Place ${label}`}</span>
          </div>
        );
      }
    } else {
      return (
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-3.5 py-2",
            "text-xs font-medium shadow-xl shadow-black/25 pointer-events-none",
            "backdrop-blur-sm transition-colors duration-100 max-w-[280px]",
            "bg-emerald-600/95 text-white",
          )}
        >
          <Plus className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{`Place ${label}`}</span>
        </div>
      );
    }
  }

  if (draggable.kind === "section") {
    isSection = true;
    const fileCount = draggable.payload.files.length;
    const noun = fileCount === 1 ? "file" : "files";
    label = `"${draggable.payload.sectionName || "Section"}" (${fileCount} ${noun})`;
  } else if (draggable.kind === "canvas-node") {
    label = draggable.payload.name || "node";
  } else if (draggable.kind === "chat-session") {
    label = draggable.payload.name || "chat";
  } else if (draggable.kind === "canvas-card") {
    label = draggable.primaryName;
  } else if (draggable.kind === "integration-connection") {
    label = draggable.primaryName;
  } else if (draggable.kind === "files") {
    const onlyFolders =
      draggable.payloads.length > 0 &&
      draggable.payloads.every((p) => p.isFolder);
    isFolderBundle = onlyFolders;
    label =
      draggable.payloads.length === 1
        ? draggable.primaryName
        : `${draggable.payloads.length} items`;
  } else {
    label = "item";
  }

  if (droppable) {
    const classification = classifyTransfer(draggable, droppable.destination);
    if (
      droppable.surface === "rail-workspace-folder" &&
      draggable.kind === "files" &&
      classification.kind === "move"
    ) {
      // Shared-with-me files copy into a workspace (the recipient can't relocate
      // them out of the owner's scope); owned files move. Mirror the dispatcher.
      const allShared =
        draggable.payloads.length > 0 &&
        draggable.payloads.every((p) => p.sharedSource);
      if (allShared) {
        variant = "copy";
        leadVerb = "Copy";
      } else {
        const dest =
          droppable.destination.kind === "workspace"
            ? droppable.destination
            : null;
        const { rejections } = dest
          ? buildWorkspaceMoves(draggable.payloads, {
              workspaceId: dest.workspaceId,
              parentPath: dest.parentPath,
            })
          : { rejections: [] as { kind: string }[] };
        if (rejections.some((r) => r.kind === "self_folder")) {
          variant = "invalid";
          label = "Can't move folder into itself";
          leadVerb = "";
        } else {
          variant = "move";
          leadVerb = "Move";
        }
      }
    } else if (classification.kind === "noop") {
      // A no-op (dropping where it already lives) keeps the NEUTRAL pill —
      // the item's name with no verb and no ring. The pill must never vanish
      // mid-drag (it reads as a broken drag); it just stops promising an action.
      variant = "neutral";
      leadVerb = "";
    } else if (classification.kind === "invalid") {
      variant = "invalid";
      label = classification.reason || "Can't drop here";
      leadVerb = "";
    } else if (
      draggable.kind === "files" &&
      draggable.payloads.length > 0 &&
      draggable.payloads.every(
        (p) => p.sourceClass === "workspace" && !p.isFolder,
      ) &&
      (droppable.surface === "canvas-board" ||
        droppable.surface === "canvas-section" ||
        droppable.surface === "canvas-scope" ||
        droppable.surface === "rail-canvas-row" ||
        droppable.surface === "files-rail-canvas-scope" ||
        droppable.surface === "chat-composer" ||
        droppable.surface === "composer-attach")
    ) {
      // Library files land in canvases and chats as references — never
      // "Copy". On the board the verb is "Place" (the drop picks a spot);
      // into a chat, which has no position, it stays "Add".
      variant = "place";
      leadVerb =
        droppable.surface === "chat-composer" ||
        droppable.surface === "composer-attach"
          ? "Add"
          : "Place";
    } else if (classification.kind === "place") {
      variant = "place";
      leadVerb = "Place";
      if (classification.parentSectionId) {
        label = `${label} into section`;
      }
    } else if (classification.kind === "place_chat") {
      variant = "place";
      leadVerb = "Place chat";
      if (classification.parentSectionId) {
        label = `${label} into section`;
      }
    } else if (classification.kind === "reparent_node") {
      variant = "move";
      leadVerb = "Move";
      label =
        classification.targetParentSectionId === null
          ? `${label} to canvas root`
          : `${label} into section`;
    } else if (classification.kind === "section_to_folder") {
      variant = "copy";
      leadVerb = "Copy";
      label = `${label} into folder`;
    } else if (classification.kind === "move_canvas") {
      variant = "move";
      leadVerb = "Move";
    } else if (classification.kind === "move_chat") {
      variant = "move";
      leadVerb = "Move";
    } else if (classification.kind === "favorite") {
      variant = "favorite";
      leadVerb = "Favorite";
    } else if (classification.kind === "move") {
      if (draggable.kind === "files") {
        // Shared-with-me files copy into a workspace (the recipient can't
        // relocate them out of the owner's scope) — mirror the dispatcher,
        // which copies shared payloads even on the move-classified surfaces.
        const allShared =
          draggable.payloads.length > 0 &&
          draggable.payloads.every((p) => p.sharedSource);
        if (allShared) {
          variant = "copy";
          leadVerb = "Copy";
        } else {
          const { rejections } = buildWorkspaceMoves(draggable.payloads, {
            workspaceId: classification.workspaceId,
            parentPath: classification.parentPath,
          });
          if (rejections.some((r) => r.kind === "self_folder")) {
            variant = "invalid";
            label = "Can't move folder into itself";
            leadVerb = "";
          } else {
            variant = "move";
            leadVerb = "Move";
          }
        }
      }
    } else if (
      draggable.kind === "files" &&
      (droppable.surface === "canvas-scope" ||
        droppable.surface === "rail-canvas-row" ||
        droppable.surface === "files-rail-canvas-scope")
    ) {
      // Pulling a file into a canvas (a working document) — copy-into-context.
      variant = "place";
      leadVerb = "";
      label = "Add to canvas";
    } else if (
      droppable.surface === "chat-composer" ||
      droppable.surface === "composer-attach"
    ) {
      // Pulling a file into a chat (existing session or the home/workspace
      // composer's pending one) — copy-into-context.
      variant = "place";
      leadVerb = "";
      label = "Add to chat";
    } else {
      const workspaceOntoCanvas =
        droppable.destination.kind === "canvas" &&
        draggable.kind === "files" &&
        draggable.payloads.every((p) => p.sourceClass === "workspace");
      if (workspaceOntoCanvas) {
        variant = "place";
        leadVerb = "Place";
        if (isFolderBundle) {
          label = `${label} on canvas`;
        }
      } else if (
        isFolderBundle &&
        droppable.destination.kind === "canvas" &&
        draggable.kind === "files"
      ) {
        variant = "place";
        leadVerb = "Place";
        label = `${label} as section`;
      } else {
        variant = "copy";
        leadVerb = "Copy";
      }
    }
  }

  // Neutral pill (no target under the cursor) answers "what am I dragging",
  // so it carries the asset's own icon — canvases frame, chats bubble,
  // folder bundles Folder, multi-file bundles Files; a single file renders
  // its real type via FileRecordTypeIcon below. Action variants keep their
  // action icons instead (the verb is the message there).
  const singleFilePayload =
    draggable.kind === "files" &&
    !isFolderBundle &&
    draggable.payloads.length === 1
      ? draggable.payloads[0]
      : null;
  const AssetIcon =
    draggable.kind === "canvas-card" || draggable.kind === "canvas-node"
      ? Frame
      : draggable.kind === "chat-session"
        ? MessagesSquare
        : isSection || isFolderBundle
          ? Folder
          : draggable.kind === "files"
            ? Files
            : Folder;

  const Icon =
    variant === "invalid"
      ? Ban
      : variant === "favorite"
        ? Star
        : variant === "move"
          ? Move
          : variant === "place"
            ? Plus
            : variant === "copy"
              ? Copy
              : AssetIcon;

  // Connectors preview as their own icon + name with no "Copy"/"Attach" verb
  // (dragging one references the connector, it doesn't duplicate a file). The
  // meaningful drop states still win: invalid/noop show Ban + reason, favorite
  // shows Star — so the pill keeps reflecting the active drop target.
  const isConnector = draggable.kind === "integration-connection";
  // Connector keeps its own icon for the "reference" states; invalid/noop/
  // favorite override with Ban/Star so the drop state still reads clearly.
  const showConnectorIcon =
    isConnector && variant !== "invalid" && variant !== "favorite";
  // Neutral = in flight with no valid target under the cursor. Show just the
  // dragged item's name (no "Copy"/"Move" verb) so the pill doesn't promise an
  // action before you've aimed anywhere.
  const suppressVerb =
    (isConnector && variant !== "favorite") || variant === "neutral";

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3.5 py-2",
        "text-xs font-medium shadow-xl shadow-black/25 pointer-events-none",
        "backdrop-blur-sm transition-colors duration-100 max-w-[280px]",
        variant === "copy" && "bg-rust/95 text-white",
        variant === "favorite" && "bg-amber-500/95 text-white",
        variant === "move" && "bg-foreground/95 text-background",
        variant === "place" && "bg-emerald-600/95 text-white",
        variant === "neutral" && "bg-foreground/80 text-background",
        // Invalid ("can't drop here") reads as a muted, grayed-out disabled
        // state with a Ban icon — nothing will happen.
        variant === "invalid" && "bg-foreground/45 text-background/70",
      )}
    >
      {draggable.kind === "integration-connection" && showConnectorIcon ? (
        <IntegrationIcon
          platform={draggable.platform}
          name={draggable.primaryName}
          imageURL={draggable.imageURL}
          className="size-3.5 shrink-0 rounded object-cover"
        />
      ) : variant === "neutral" && singleFilePayload ? (
        <FileRecordTypeIcon
          contentType={singleFilePayload.contentType}
          className="h-3.5 w-3.5 shrink-0"
        />
      ) : (
        <Icon className="h-3.5 w-3.5 shrink-0" />
      )}
      <span className="truncate">
        {leadVerb && !suppressVerb ? `${leadVerb} ${label}` : label}
      </span>
    </div>
  );
}

/** @deprecated Use `TransferDragPill`. */
export const CanvasDragPill = TransferDragPill;
